import {
	type OrderSampleAttachmentType,
	useGenerateSampleAttachmentUploadUrlMutation,
	useConfirmSampleAttachmentUploadsMutation,
} from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { prepareFiles } from '../lib/file-size';
import { SAMPLE_MEDIA_ACCEPT } from '../lib/file-types';

const WEB_SAFE_IMAGE_TYPES = new Set([
	'image/jpeg',
	'image/png',
	'image/gif',
	'image/webp',
	'image/svg+xml',
	'image/avif',
	'image/bmp',
]);

function isWebSafeImage(mimeType: string): boolean {
	return WEB_SAFE_IMAGE_TYPES.has(mimeType.toLowerCase());
}

interface PendingFile {
	file: File;
	preview: string;
}

export function useSampleAttachmentUpload() {
	const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
	const [uploading, setUploading] = useState(false);
	const [converting, setConverting] = useState(false);
	const [generateUrl] = useGenerateSampleAttachmentUploadUrlMutation();
	const [confirmUploads] = useConfirmSampleAttachmentUploadsMutation();

	const addFiles = async (files: FileList | File[]) => {
		setConverting(true);
		try {
			const prepared = await prepareFiles(files, SAMPLE_MEDIA_ACCEPT);
			const newFiles = prepared.map((file) => ({
				file,
				preview: isWebSafeImage(file.type) ? URL.createObjectURL(file) : '',
			}));
			if (!newFiles.length) return;
			setPendingFiles((prev) => [...prev, ...newFiles]);
		} finally {
			setConverting(false);
		}
	};

	const removeFile = (index: number) => {
		setPendingFiles((prev) => {
			const next = [...prev];
			if (next[index].preview) URL.revokeObjectURL(next[index].preview);
			next.splice(index, 1);
			return next;
		});
	};

	const reset = () => {
		pendingFiles.forEach((f) => {
			if (f.preview) URL.revokeObjectURL(f.preview);
		});
		setPendingFiles([]);
		setUploading(false);
	};

	const uploadAll = async (
		sampleId: string,
		type: OrderSampleAttachmentType
	): Promise<boolean> => {
		if (pendingFiles.length === 0) return true;
		setUploading(true);

		try {
			const confirmed: Array<{ name: string; url: string; mimeType: string; size: number }> =
				[];

			for (const { file } of pendingFiles) {
				const { data: urlData } = await generateUrl({
					variables: {
						input: {
							orderItemSampleId: sampleId,
							type,
							fileName: file.name,
							fileType: file.type,
							fileSize: file.size,
						},
					},
				});

				const upload = urlData?.generateSampleAttachmentUploadUrl;
				if (!upload?.uploadUrl || !upload.blobUrl) {
					throw new Error(`Failed to get upload URL for ${file.name}`);
				}

				const res = await fetch(upload.uploadUrl, {
					method: 'PUT',
					body: file,
					headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
				});
				if (!res.ok) throw new Error(`Failed to upload ${file.name}`);

				confirmed.push({
					name: file.name,
					url: upload.blobUrl,
					mimeType: file.type,
					size: file.size,
				});
			}

			await confirmUploads({
				variables: {
					input: {
						orderItemSampleId: sampleId,
						type,
						files: confirmed,
					},
				},
			});

			reset();
			return true;
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to upload files');
			setUploading(false);
			return false;
		}
	};

	return { pendingFiles, addFiles, removeFile, reset, uploadAll, uploading, converting };
}
